import { Toast, type ToastTone } from '@ecom/ui-kit';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * `@ecom/ui-kit`'s `Toast` is a single controlled message (§7.1 primitives —
 * no queue built in, by design: a queue is an application concern, not a
 * design-system one). This is that missing queue, built once here rather
 * than in every page that needs to confirm a mutation.
 */
interface QueuedToast {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<readonly QueuedToast[]>([]);

  const push = useCallback((message: string, tone: ToastTone) => {
    nextId += 1;
    const id = nextId;
    setToasts((current) => [...current, { id, message, tone }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message: string) => {
        push(message, 'good');
      },
      error: (message: string) => {
        push(message, 'critical');
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              open
              message={toast.message}
              tone={toast.tone}
              onDismiss={() => {
                dismiss(toast.id);
              }}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error('useToast() must be used within a <ToastProvider>.');
  return ctx;
}
